import { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import {
  Stack, 
  Typography,
  Container,
  Card,
  CardContent,
  Box,
  LinearProgress,
  Button
} from '@mui/material';

import DoneAllIcon from '@mui/icons-material/DoneAll';
import EmailIcon from '@mui/icons-material/Email';

import { AccountContext } from '../account';

function SignatureFinishedPage() {
  const navigate = useNavigate();
  const { getSession } = useContext(AccountContext);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(7); // 7 segundos
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const timerRef = useRef(null);
  const getSessionRef = useRef(getSession);

  // Atualizar ref quando getSession mudar
  useEffect(() => {
    getSessionRef.current = getSession;
  }, [getSession]);

  // Verificar autenticação ao montar o componente
  useEffect(() => {
    const checkAuthentication = async () => {
      try {
        await getSessionRef.current();
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
      } finally {
        setIsCheckingAuth(false);
      }
    };

    checkAuthentication();
  }, []);

  // Timer e redirecionamento
  useEffect(() => {
    if (isCheckingAuth) {
      return () => {
        // Cleanup vazio quando ainda está verificando autenticação
      };
    }

    // Atualizar contador a cada segundo
    timerRef.current = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          // Redirecionar baseado na autenticação
          if (isAuthenticated) {
            navigate('/dashboard/app');
          } else {
            navigate('/login');
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Cleanup
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isAuthenticated, isCheckingAuth, navigate]);

  const handleRedirectNow = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    if (isAuthenticated) {
      navigate('/dashboard/app');
    } else {
      navigate('/login');
    }
  };

  return (
    <>
      <Helmet>
        <title>Assinatura Concluída</title>
      </Helmet>

      <Container maxWidth="sm" sx={{ py: { xs: 4, sm: 6, md: 8 } }}>
        <Stack spacing={4} alignItems="center" justifyContent="center" sx={{ minHeight: '60vh' }}>
          {/* Ícone de sucesso */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: { xs: 80, sm: 100, md: 120 },
              height: { xs: 80, sm: 100, md: 120 },
              borderRadius: '50%',
              bgcolor: 'success.lighter',
              mb: 2
            }}
          >
            <DoneAllIcon 
              color="success" 
              sx={{ 
                fontSize: { xs: 60, sm: 80, md: 100 } 
              }} 
            />
          </Box>

          {/* Mensagem principal */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography 
              variant="h4" 
              gutterBottom
              sx={{
                fontWeight: 600,
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                mb: 1
              }}
            >
              Assinatura realizada com sucesso!
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}
            >
              Seu documento foi assinado e está sendo processado.
            </Typography>
          </Box>

          {/* Card com aviso sobre email */}
          <Card 
            sx={{ 
              width: '100%',
              bgcolor: 'info.lighter',
              border: '1px solid',
              borderColor: 'info.main',
              boxShadow: 3
            }}
          >
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <EmailIcon 
                  color="info" 
                  sx={{ 
                    fontSize: { xs: 28, sm: 32 },
                    mt: 0.5,
                    flexShrink: 0
                  }} 
                />
                <Box sx={{ flex: 1 }}>
                  <Typography 
                    variant="h6" 
                    gutterBottom
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: '1rem', sm: '1.125rem' },
                      mb: 1
                    }}
                  >
                    Você receberá uma cópia por email
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{
                      fontSize: { xs: '0.813rem', sm: '0.875rem' },
                      lineHeight: 1.6
                    }}
                  >
                    Quando todas as assinaturas forem concluídas, você receberá uma cópia do documento assinado no endereço de email cadastrado.
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{
                      fontSize: { xs: '0.813rem', sm: '0.875rem' },
                      lineHeight: 1.6,
                      mt: 1,
                      fontWeight: 500
                    }}
                  >
                    Caso não encontre, lembre-se de checar também a sua caixa de spam e/ou lixeira.
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* Indicador de redirecionamento */}
          <Box sx={{ width: '100%', maxWidth: 400 }}>
            <Stack spacing={2} alignItems="center">
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                Redirecionando em {timeRemaining} segundo{timeRemaining !== 1 ? 's' : ''}...
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={(7 - timeRemaining) / 7 * 100} 
                sx={{ 
                  width: '100%',
                  height: 6,
                  borderRadius: 3,
                  bgcolor: 'grey.200',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 3
                  }
                }}
              />
              <Button
                variant="outlined"
                onClick={handleRedirectNow}
                sx={{
                  mt: 1,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }}
              >
                Ir agora
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Container>
    </>
  );
}

export default SignatureFinishedPage;
